import { Injectable } from '@angular/core';
import { NGXLogger as LoggerService } from 'ngx-logger';
import { environment } from '../../../environments/environment';
import {
    RewardedAdOptions,
    RewardedAdProvider,
    RewardedAdResult,
    RewardedAdStatus,
} from './rewarded-ad.types';

/** AppLixir web SDK v6 (https://support.applixir.com — "Step 3: Initializing Video Ad"). */
const APPLIXIR_SCRIPT_URL = 'https://cdn.applixir.com/applixir.app.v6.0.1.js';

/**
 * AppLixir v6 is built on the Google IMA HTML5 SDK and references the global
 * `google.ima` at init — without it the SDK throws "ReferenceError: google is
 * not defined". So IMA must be loaded BEFORE AppLixir.
 */
const IMA_SDK_URL = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';

/**
 * If the SDK has not talked to us at all (no status, no error) within this
 * window, give up and settle 'no-ad'. Generous on purpose: the Didomi consent
 * dialog (EU first visit) opens ON TOP of the player and the SDK emits nothing
 * while the user deliberates — a short timeout would flash the error toast
 * under the consent dialog. After the first callback the watchdog switches to
 * the sliding inactivity window below.
 */
const FIRST_SIGNAL_TIMEOUT_MS = 120_000;

/**
 * Sliding watchdog after the first signal: every SDK callback re-arms it. A
 * playing video keeps emitting quartile events well inside this window, so a
 * healthy flow never trips it — but a flow that stalls mid-way (or emits only
 * tokens outside the terminal lists) settles 'error' instead of leaving the
 * CTA stuck on "Loading ad…" forever.
 */
const SIGNAL_INACTIVITY_TIMEOUT_MS = 120_000;

/**
 * Delay before the per-call anchor div is removed after the flow settles. The
 * SDK's post-roll ("Thanks for watching!", default 2500 ms) is still on screen
 * when the 'complete' status fires — removing the anchor immediately would cut
 * it off. 5 s safely covers it; on every other exit path the SDK has already
 * hidden its overlay by the time we settle.
 */
const ANCHOR_CLEANUP_DELAY_MS = 5_000;

declare global {
    interface Window {
        initializeAndOpenPlayer?: (options: {
            apiKey: string;
            injectionElementId: string;
            /** Suppresses the SDK's own "No ad available" toast — the CTA owns error feedback. */
            disableDefaultErrorToast?: boolean;
            adStatusCallbackFn: (status: unknown) => void;
            adErrorCallbackFn: (error: unknown) => void;
        }) => Promise<unknown>;
    }
}

// Status mapping, verified against the v6.0.1 SDK source (see handover §13):
// the IMA path forwards raw google.ima.AdEvent types ({ type: 'complete' |
// 'allAdsCompleted' | 'skip' | 'userClose' | ...}), the SDK's own exits emit
// 'manuallyEnded' (close button + confirm), 'thankYouModalClosed' (after the
// post-roll) and 'consentDeclined' (Didomi CMP). Older-SDK tokens are kept
// defensively. Every raw status is logged so new values can be spotted live.
const COMPLETE = ['ad-watched', 'fb-watched', 'complete', 'alladscompleted'];
const ADBLOCKED = ['ad-blocker', 'ad-blocked', 'adblock', 'adblocked'];
const NO_AD = ['ads-unavailable', 'no-ad', 'no-fill', 'ad-unavailable', 'no-zoneid'];
const SKIPPED = ['ad-interrupted', 'skipped', 'skip', 'user-exited', 'user-cancel'];
const CLOSED = ['sys-closing', 'closed', 'close', 'manuallyended', 'userclose', 'consentdeclined', 'thankyoumodalclosed'];
const ERRORED = ['network-error', 'cors-error', 'error'];

/**
 * The real provider. Loads the AppLixir SDK (once, lazily on first use) and
 * opens a rewarded video. The v6 player renders as its OWN fixed fullscreen
 * modal — this provider only supplies a throwaway anchor div (fresh per call,
 * removed afterwards). Fresh-per-call matters: the SDK appends its markup with
 * `innerHTML +=` on every init, so reusing one anchor would stack duplicate
 * DOM ids and break the second ad of a session.
 *
 * Reward is granted only on a fully-watched status; every other terminal
 * status resolves without a grant. Exception: the SDK cannot be aborted, so a
 * flow settled by watchdog timeout may still finish — a late 'complete' then
 * fires `options.onLateComplete` so the caller can grant after all.
 * AppLixir is the seller-of-record (own sellers.json), not pub-7708.
 * See handover §2b / §13.
 */
@Injectable()
export class AppLixirRewardedAdProvider extends RewardedAdProvider {
    private scriptPromise?: Promise<void>;
    private anchorCounter = 0;

    constructor(private logger: LoggerService) {
        super();
    }

    showRewardedAd(options?: RewardedAdOptions): Promise<RewardedAdResult> {
        return this.ensureScriptLoaded()
            .then(() => this.openPlayer(options))
            .catch((error) => {
                this.logger.error('AppLixirRewardedAdProvider: SDK load failed', error);
                return { status: 'error' as RewardedAdStatus };
            });
    }

    private ensureScriptLoaded(): Promise<void> {
        if (this.scriptPromise) {
            return this.scriptPromise;
        }
        // IMA first (AppLixir depends on the global `google.ima`), then AppLixir.
        this.scriptPromise = this.loadScriptOnce(IMA_SDK_URL, () => !!(window as any).google?.ima)
            .then(() => this.loadScriptOnce(APPLIXIR_SCRIPT_URL, () => typeof window.initializeAndOpenPlayer === 'function'))
            .catch((error) => {
                // Never cache a failed load — a transient failure (network blip,
                // adblocker) would otherwise dead-end every retry until full reload.
                this.scriptPromise = undefined;
                throw error;
            });
        return this.scriptPromise;
    }

    private loadScriptOnce(url: string, alreadyLoaded: () => boolean): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (alreadyLoaded()) {
                resolve();
                return;
            }
            const existing = document.querySelector(`script[src="${url}"]`) as HTMLScriptElement | null;
            const script = existing ?? document.createElement('script');
            script.addEventListener('load', () => resolve(), { once: true });
            script.addEventListener('error', (event) => {
                // Drop the dead tag: its load/error events never re-fire, so a
                // retry must re-create it from scratch.
                script.remove();
                reject(event);
            }, { once: true });
            if (!existing) {
                script.src = url;
                script.async = true;
                document.head.appendChild(script);
            }
        });
    }

    /** Throwaway anchor for the SDK markup; the visible player is position:fixed anyway. */
    private createAnchor(): HTMLElement {
        const anchor = document.createElement('div');
        anchor.id = `applixir-anchor-${++this.anchorCounter}`;
        document.body.appendChild(anchor);
        return anchor;
    }

    private openPlayer(options?: RewardedAdOptions): Promise<RewardedAdResult> {
        return new Promise<RewardedAdResult>((resolve) => {
            const open = window.initializeAndOpenPlayer;
            if (typeof open !== 'function') {
                resolve({ status: 'error' });
                return;
            }

            const anchor = this.createAnchor();
            let settled = false;
            let settledByWatchdog = false;
            let watchdogTimer: ReturnType<typeof setTimeout> | undefined;

            // The SDK cannot be aborted (no close/destroy API), so a watchdog
            // settle may race a still-live flow (consent dialog open, slow VAST).
            // The FIRST terminal status arriving after such a settle decides:
            // 'complete' -> the user did watch the whole ad, grant it after all
            // (onLateComplete); anything else -> just clean up the anchor.
            const handleLateStatus = (status: RewardedAdStatus) => {
                if (!settledByWatchdog) {
                    return; // regular duplicate terminal status ('complete' + 'alladscompleted') — ignore
                }
                settledByWatchdog = false; // only the first late terminal status counts
                clearTimeout(watchdogTimer); // a re-armed inactivity timer is obsolete now
                this.logger.log('AppLixirRewardedAdProvider: late terminal status after watchdog settle', status);
                setTimeout(() => anchor.remove(), ANCHOR_CLEANUP_DELAY_MS);
                if (status === 'complete') {
                    options?.onLateComplete?.();
                }
            };

            const settle = (status: RewardedAdStatus, fromWatchdog = false) => {
                if (settled) {
                    handleLateStatus(status);
                    return;
                }
                settled = true;
                settledByWatchdog = fromWatchdog;
                clearTimeout(watchdogTimer);
                this.logger.log('AppLixirRewardedAdProvider: settle', status);
                if (!fromWatchdog) {
                    // Deferred so the SDK's post-roll can finish before its DOM goes away.
                    setTimeout(() => anchor.remove(), ANCHOR_CLEANUP_DELAY_MS);
                }
                // On a watchdog settle the SDK may still be alive and needs its
                // injected DOM — keep the anchor; handleLateStatus removes it if
                // a terminal status arrives after all (else it leaks one empty div).
                resolve({ status });
            };

            const armWatchdog = (timeoutMs: number, timeoutStatus: RewardedAdStatus, warnMessage: string) => {
                clearTimeout(watchdogTimer);
                watchdogTimer = setTimeout(() => {
                    this.logger.warn(`AppLixirRewardedAdProvider: ${warnMessage}`);
                    settle(timeoutStatus, true);
                }, timeoutMs);
            };
            const onSignal = () =>
                armWatchdog(SIGNAL_INACTIVITY_TIMEOUT_MS, 'error', 'SDK signal stream stalled, giving up');
            armWatchdog(FIRST_SIGNAL_TIMEOUT_MS, 'no-ad', 'no SDK signal, giving up');

            const openResult = open({
                apiKey: environment.applixirApiKey,
                injectionElementId: anchor.id,
                disableDefaultErrorToast: true,
                adStatusCallbackFn: (status: unknown) => {
                    onSignal();
                    this.logger.log('AppLixir raw status', status);
                    const token = this.normalize(status);
                    if (COMPLETE.includes(token)) {
                        settle('complete');
                    } else if (ADBLOCKED.includes(token)) {
                        settle('adblocked');
                    } else if (NO_AD.includes(token)) {
                        settle('no-ad');
                    } else if (SKIPPED.includes(token)) {
                        settle('skipped');
                    } else if (CLOSED.includes(token)) {
                        settle('closed');
                    } else if (ERRORED.includes(token)) {
                        settle('error');
                    }
                    // non-terminal (loaded / start / quartiles / …) -> wait for the next status
                },
                adErrorCallbackFn: (error: unknown) => {
                    this.logger.warn('AppLixir error', this.extractError(error));
                    settle('error');
                },
            });
            // initializeAndOpenPlayer is async and can reject (e.g. VAST config
            // fetch fails) — without this catch the flow would hang unsettled.
            Promise.resolve(openResult).catch((error) => {
                this.logger.error('AppLixirRewardedAdProvider: initializeAndOpenPlayer failed', error);
                settle('error');
            });
        });
    }

    /** Reduce a string / { type } / { status } payload to a lowercase token. */
    private normalize(status: unknown): string {
        const raw =
            typeof status === 'string'
                ? status
                : ((status as any)?.type ?? (status as any)?.status ?? '');
        return String(raw).toLowerCase();
    }

    private extractError(error: unknown): unknown {
        try {
            return (error as any)?.getError?.().data ?? error;
        } catch {
            return error;
        }
    }
}
